(() => {
  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (_error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const initialize = (section) => {
    if (section.dataset.initialized === 'true') return;

    const variantsNode = section.querySelector('[data-product-variants]');
    const durationPosition = Number(section.dataset.durationPosition) - 1;
    const dataPosition = Number(section.dataset.dataPosition) - 1;

    if (!variantsNode || durationPosition < 0 || dataPosition < 0) return;

    let variants;
    try {
      variants = JSON.parse(variantsNode.textContent);
    } catch (_error) {
      return;
    }

    const durationButtons = [...section.querySelectorAll('[data-option-group="duration"] [data-option-value]')];
    const dataButtons = [...section.querySelectorAll('[data-option-group="data"] [data-option-value]')];
    const variantInput = section.querySelector('[data-variant-id]');
    const addButton = section.querySelector('[data-add-button]');
    const addLabel = section.querySelector('[data-add-label]');
    const buyNowButton = section.querySelector('[data-buy-now-button]');
    const buyNowLabel = section.querySelector('[data-buy-now-label]');
    const quantityInput = section.querySelector('[data-quantity-input]');
    const quantityDecrease = section.querySelector('[data-quantity-decrease]');
    const quantityIncrease = section.querySelector('[data-quantity-increase]');
    const summaryDuration = section.querySelector('[data-summary-duration]');
    const summaryData = section.querySelector('[data-summary-data]');
    const summaryPrice = section.querySelector('[data-summary-price]');
    const planNote = section.querySelector('[data-plan-note]');
    const checkoutError = section.querySelector('[data-checkout-error]');
    const mobileBar = section.querySelector('[data-mobile-buy]');
    const mobileSelection = section.querySelector('[data-mobile-selection]');
    const mobilePrice = section.querySelector('[data-mobile-price]');
    const mobileQuantity = section.querySelector('[data-mobile-quantity]');
    const mobileQuantityDecrease = section.querySelector('[data-mobile-quantity-decrease]');
    const mobileQuantityIncrease = section.querySelector('[data-mobile-quantity-increase]');
    const mobileAdd = section.querySelector('[data-mobile-add]');
    const mobileBuyNow = section.querySelector('[data-mobile-buy-now]');
    const currency = window.Shopify?.currency?.active || document.documentElement.dataset.currency || 'USD';
    const addText = section.dataset.addLabel || 'Add to cart';
    const buyNowText = section.dataset.buyLabel || 'Buy now';
    const soldOutText = section.dataset.soldOutLabel || 'Sold out';

    const defaultDuration = section.dataset.defaultDuration;
    const defaultData = section.dataset.defaultData;
    const requestedVariantId = Number(new URL(window.location.href).searchParams.get('variant'));
    const requestedVariant = variants.find((variant) => variant.id === requestedVariantId && variant.available);
    const preferredVariant = variants.find((variant) =>
      variant.available &&
      variant.options[durationPosition] === defaultDuration &&
      variant.options[dataPosition] === defaultData
    );
    const initialVariant = requestedVariant || preferredVariant || variants.find((variant) => variant.available) || variants[0];

    if (!initialVariant) return;

    const selection = {
      duration: initialVariant.options[durationPosition],
      data: initialVariant.options[dataPosition],
    };

    const matchingVariant = (duration, data) => variants.find((variant) =>
      variant.options[durationPosition] === duration && variant.options[dataPosition] === data
    );

    const planDescription = (data) => {
      const normalized = data.trim().toLowerCase();
      const amount = data.match(/\d+(?:\.\d+)?\s*(?:gb|mb)/i)?.[0]?.replace(/\s+/g, '') || data;

      if (normalized.includes('unlimited')) {
        return '2GB high-speed data per day · Then unlimited at 5 Mbps';
      }

      if (normalized.includes('/day') || normalized.includes('per day')) {
        return `${amount} high-speed data per day · Then unlimited at 128 Kbps`;
      }

      return `${amount} high-speed data in total · Then unlimited at 128 Kbps`;
    };

    const getQuantity = () => Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);

    const setQuantity = (value) => {
      const quantity = Math.max(1, Number.parseInt(String(value), 10) || 1);
      if (quantityInput) quantityInput.value = String(quantity);
      if (mobileQuantity) mobileQuantity.textContent = String(quantity);
      if (quantityDecrease) quantityDecrease.disabled = quantity <= 1;
      if (mobileQuantityDecrease) mobileQuantityDecrease.disabled = quantity <= 1;
    };

    const setPressed = (buttons, value) => {
      buttons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.optionValue === value));
      });
    };

    const updateDataCards = () => {
      dataButtons.forEach((button) => {
        const variant = matchingVariant(selection.duration, button.dataset.optionValue);
        const price = button.querySelector('[data-option-price]');
        const available = Boolean(variant?.available);
        button.hidden = !available;
        button.disabled = !available;
        if (price) price.textContent = variant ? formatMoney(variant.price, currency) : 'Unavailable';
      });

      const selectedDataButton = dataButtons.find((button) => button.dataset.optionValue === selection.data);
      if (!selectedDataButton || selectedDataButton.hidden || selectedDataButton.disabled) {
        const firstAvailableData = dataButtons.find((button) => !button.hidden && !button.disabled);
        if (firstAvailableData) selection.data = firstAvailableData.dataset.optionValue;
      }
    };

    const updateDurationButtons = () => {
      durationButtons.forEach((button) => {
        button.disabled = !variants.some((variant) =>
          variant.available && variant.options[durationPosition] === button.dataset.optionValue
        );
      });
    };

    const render = () => {
      updateDurationButtons();
      updateDataCards();
      setPressed(durationButtons, selection.duration);
      setPressed(dataButtons, selection.data);

      const variant = matchingVariant(selection.duration, selection.data);
      const available = Boolean(variant?.available);
      const price = variant ? formatMoney(variant.price, currency) : '—';

      if (variantInput && variant) variantInput.value = variant.id;
      if (summaryDuration) summaryDuration.textContent = selection.duration;
      if (summaryData) summaryData.textContent = selection.data;
      if (summaryPrice) summaryPrice.textContent = price;
      if (planNote) planNote.textContent = planDescription(selection.data);
      if (mobileSelection) mobileSelection.textContent = `${selection.duration} · ${selection.data}`;
      if (mobilePrice) mobilePrice.textContent = price;
      if (mobileBar) mobileBar.hidden = false;

      if (addButton) addButton.disabled = !available;
      if (buyNowButton) buyNowButton.disabled = !available;
      if (mobileAdd) mobileAdd.disabled = !available;
      if (mobileBuyNow) mobileBuyNow.disabled = !available;
      if (addLabel) addLabel.textContent = available ? addText : soldOutText;
      if (buyNowLabel) buyNowLabel.textContent = available ? buyNowText : soldOutText;
      if (mobileAdd) mobileAdd.textContent = available ? addText : soldOutText;
      if (mobileBuyNow) mobileBuyNow.textContent = available ? buyNowText : soldOutText;

      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    };

    const buyNow = async () => {
      const variant = matchingVariant(selection.duration, selection.data);
      if (!variant?.available || !buyNowButton) return;

      const root = window.Shopify?.routes?.root || '/';
      const cartAddUrl = `${root}cart/add.js`;
      const checkoutUrl = `${root}checkout`;

      if (checkoutError) {
        checkoutError.hidden = true;
        checkoutError.textContent = '';
      }

      buyNowButton.disabled = true;
      if (mobileBuyNow) mobileBuyNow.disabled = true;
      if (buyNowLabel) buyNowLabel.textContent = 'Redirecting…';
      if (mobileBuyNow) mobileBuyNow.textContent = 'Redirecting…';

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: getQuantity() }],
          }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'Unable to start checkout.');
        }

        window.location.assign(checkoutUrl);
      } catch (error) {
        if (checkoutError) {
          checkoutError.textContent = error.message || 'Unable to start checkout. Please try again.';
          checkoutError.hidden = false;
        }
        buyNowButton.disabled = false;
        if (mobileBuyNow) mobileBuyNow.disabled = false;
        if (buyNowLabel) buyNowLabel.textContent = buyNowText;
        if (mobileBuyNow) mobileBuyNow.textContent = buyNowText;
      }
    };

    durationButtons.forEach((button) => {
      button.addEventListener('click', () => {
        selection.duration = button.dataset.optionValue;
        render();
      });
    });

    dataButtons.forEach((button) => {
      button.addEventListener('click', () => {
        selection.data = button.dataset.optionValue;
        render();
      });
    });

    quantityDecrease?.addEventListener('click', () => setQuantity(getQuantity() - 1));
    quantityIncrease?.addEventListener('click', () => setQuantity(getQuantity() + 1));
    quantityInput?.addEventListener('change', () => setQuantity(getQuantity()));
    mobileQuantityDecrease?.addEventListener('click', () => setQuantity(getQuantity() - 1));
    mobileQuantityIncrease?.addEventListener('click', () => setQuantity(getQuantity() + 1));
    mobileAdd?.addEventListener('click', () => addButton?.click());
    buyNowButton?.addEventListener('click', buyNow);
    mobileBuyNow?.addEventListener('click', buyNow);

    section.dataset.initialized = 'true';
    setQuantity(1);
    render();
  };

  const initializeAll = (root = document) => {
    root.querySelectorAll('.sky-product[data-section-id]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
